"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface ToggleOption<T extends string> {
	value: T;
	label: string;
	icon: React.ReactNode;
}

interface ExpandableToggleProps<T extends string> {
	options: ToggleOption<T>[];
	value: T;
	onChange: (value: T) => void;
	onExpandChange?: (expanded: boolean) => void;
	autoCollapseDelay?: number;
	switchCollapseDelay?: number;
	className?: string;
}

export function ExpandableToggle<T extends string>({
	options,
	value,
	onChange,
	onExpandChange,
	autoCollapseDelay = 10000,
	switchCollapseDelay = 500,
	className = "",
}: ExpandableToggleProps<T>) {
	const [isExpanded, setIsExpanded] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Notify parent of expand state changes
	const updateExpanded = useCallback(
		(expanded: boolean) => {
			setIsExpanded(expanded);
			onExpandChange?.(expanded);
		},
		[onExpandChange],
	);

	// Clear timeout when component unmounts
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleButtonClick = useCallback(
		(newValue: T) => {
			// If not expanded yet, first click should just expand
			if (!isExpanded) {
				updateExpanded(true);

				// Set auto-close timer
				timeoutRef.current = setTimeout(() => {
					updateExpanded(false);
				}, autoCollapseDelay);
				return;
			}

			// If expanded, handle clicks
			if (isExpanded) {
				// Clear the auto-close timer
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
					timeoutRef.current = null;
				}

				// If clicking the same value, just collapse
				if (newValue === value) {
					updateExpanded(false);
					return;
				}

				// If clicking different value, switch then collapse
				onChange(newValue);

				// Collapse after short delay
				setTimeout(() => {
					updateExpanded(false);
				}, switchCollapseDelay);
			}
		},
		[isExpanded, value, onChange, updateExpanded, autoCollapseDelay, switchCollapseDelay],
	);

	return (
		<div
			className={`flex bg-white/80 backdrop-blur-md rounded-full p-1 shadow-lg border border-white/20 transition-all duration-300 ease-out ${className}`}
			style={{
				WebkitBackfaceVisibility: "hidden",
				backfaceVisibility: "hidden",
				transform: "translateZ(0)",
				backgroundColor: "rgba(255, 255, 255, 0.8)",
			}}
		>
			{options.map(option => (
				<button
					key={option.value}
					type="button"
					aria-label={option.label}
					aria-pressed={value === option.value}
					onClick={() => handleButtonClick(option.value)}
					className={`h-8 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
						isExpanded ? "px-3 gap-2" : "px-2 gap-0"
					} ${value === option.value ? "bg-primary text-white shadow-md" : "text-gray-600 hover:text-gray-800"}`}
					style={{
						color: value === option.value ? "#ffffff" : "#4b5563",
						backgroundColor: value === option.value ? "#ef4444" : "transparent",
					}}
				>
					<span className="flex-shrink-0" aria-hidden="true">
						{option.icon}
					</span>
					<span
						className={`text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden ${
							isExpanded ? "max-w-24 opacity-100" : "max-w-0 opacity-0"
						}`}
						aria-hidden="true"
					>
						{option.label}
					</span>
				</button>
			))}
		</div>
	);
}
